<template>
  <div class="map-container">
    <div
      class="locations-map"
      ref="locationsMap"
      v-if="locationMarkers?.length"
    >
      <GoogleMap
        :api-key="config.public.googleApiKey"
        class="custom-google-map"
        ref="googleMap"
        map-id="listMap"
        :center="center"
        :fullscreenControl="false"
        :mapTypeControl="false"
        :streetViewControl="false"
        :zoomControl="true"
        :rotateControl="false"
        :scaleControl="false"
        :zoom="zoom"
      >
        <MarkerCluster>
          <Marker
            v-for="marker in locationMarkers"
            :key="marker.uuid"
            :options="{
              position: marker.coordinates,
              icon: getMarkerIcon(marker)
            }"
            @click="setMarkerSelected(marker)"
          >
          </Marker>
        </MarkerCluster>
        <div v-if="markerSelected" class="marker-info-window">
          <div class="info-window-content">
            <div
              class="image"
              :class="markerSelected.entity.image ? 'with-image' : ''"
            >
              <img
                v-if="markerSelected.entity.image"
                :src="markerSelected.entity.image"
              />
              <img
                v-else
                src="/logos/PALC_Marque_Gold.svg"
                alt="logo"
                class="logo-img"
              />
            </div>
            <div class="text">
              <div class="info-window-title">
                <span class="heading6">{{ markerSelected.entity.title }}</span>
              </div>
              <div class="info-window-date">
                <span class="caption" v-if="markerSelected.entity.location">{{
                  markerSelected.entity.location
                }}</span>
                <span class="caption" v-if="markerSelected.entity.date">{{
                  markerSelected.entity.date
                }}</span>
                <span
                  class="caption"
                  v-if="markerSelected.entity.number_of_plans"
                  >{{ markerSelected.entity.number_of_plans }}</span
                >
              </div>
            </div>
          </div>
        </div>
      </GoogleMap>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { GoogleMap, Marker, MarkerCluster, InfoWindow } from 'vue3-google-map'

export default defineComponent({
  name: 'LocationsMap',
  props: {
    items: {
      type: Array,
      default: () => []
    }
  },
  components: {
    GoogleMap,
    Marker,
    MarkerCluster,
    InfoWindow
  },
  setup() {
    const { width, height } = useBreakpoints()
    const { t } = useI18n()
    const { $sentry } = useNuxtApp()
    const router = useRouter()
    const route = useRoute()
    const config = useRuntimeConfig()

    const initialVariables = [getGoogleInitialVariables()]

    let objectToReturn = {
      width,
      height,
      t,
      router,
      route,
      $sentry,
      config
    }
    for (const variable of initialVariables) {
      objectToReturn = Object.assign(objectToReturn, variable)
    }

    return objectToReturn
  },
  data() {
    return {
      markerSelected: null,
      zoom: 6,
      center: this.defaultCenter
    }
  },
  computed: {
    locationMarkers() {
      return (
        this.items
          ?.filter((item) => item?.coordinates)
          ?.map((item) => {
            return {
              uuid: item.uuid,
              coordinates: item.coordinates,
              type: item.type,
              entity: item
            }
          }) ?? []
      )
    }
  },
  mounted() {
    this.init()
    document.addEventListener('fullscreenchange', () => {
      this.markerSelected = null
    })
  },
  methods: {
    setMarkerSelected(event) {
      if (event?.uuid === this.markerSelected?.uuid) {
        this.markerSelected = null
      } else {
        this.markerSelected = event
      }
    },
    getMarkerIcon(marker) {
      if (this.markerSelected?.uuid === marker.uuid) {
        return `/markers/pin-${
          marker.type === 'experience' ? 'palc' : marker.type
        }-selected.png`
      } else {
        return `/markers/pin-${
          marker.type === 'experience' ? 'palc' : marker.type
        }.png`
      }
    },
    fullscreen() {
      this.$refs.locationsMap.requestFullscreen()
      setTimeout(() => {
        this.init()
      }, 100)
    },
    init() {
      this.setBounds(this, this.locationMarkers)
      this.center =
        this.$refs.googleMap?.map?.getCenter()?.lat() &&
        this.$refs.googleMap?.map.getCenter()?.lng()
          ? {
              lat: this.$refs.googleMap?.map.getCenter().lat(),
              lng: this.$refs.googleMap?.map.getCenter().lng()
            }
          : this.defaultCenter
      this.zoom = this.$refs.googleMap?.map?.getZoom()
        ? this.$refs.googleMap?.map.getZoom()
        : 6
    }
  }
})
</script>

<style scoped lang="scss">
.map-container {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: left;
  background-color: var(--brand-off-white);
  .locations-map {
    width: 100%;
    height: 0;
    :deep(.custom-google-map) {
      height: 100%;
      .marker-info-window {
        width: 327px;
        height: 122px;
        padding: 0 !important;
        background-color: var(--brand-white);
        position: absolute;
        @media (min-width: 1100px) {
          bottom: 32px;
        }
        @media (max-width: 1100px) {
          bottom: 24px;
        }
        left: calc(50vw - 163px);
        .info-window-content {
          position: relative;
          .image {
            position: absolute;
            top: 0;
            width: 120px;
            height: 120px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: var(--action-hover);
            img {
              width: 119px;
              height: 119px;
            }
            &:not(.with-image) {
              img {
                width: 40px;
                height: 40px;
              }
            }
          }
          .text {
            width: calc(100% - 128px);
            position: absolute;
            left: 128px;
            .info-window-title {
              margin-top: 12px;
              max-width: 241px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              height: 26px;
              .heading6 {
                color: var(--brand-off-black);
              }
            }
            .info-window-date {
              margin-top: 8px;
            }
          }
        }
      }
    }
  }
}
</style>